import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Tag } from 'primereact/tag';
import { Avatar } from 'primereact/avatar';
import { TabView, TabPanel } from 'primereact/tabview';
import { itTicketsAPI } from '../services/api';
import { ITTicket, ITTicketStatus } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { showCustomAlert } from './CustomAlert';

const ITTicketDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState<ITTicket | null>(null);
  const [loading, setLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentText, setEditCommentText] = useState('');

  useEffect(() => {
    if (id) {
      loadTicket();
    }
  }, [id]);

  const loadTicket = async () => {
    setLoading(true);
    try {
      const data = await itTicketsAPI.getById(id!);
      console.log('Loaded ticket:', data);
      setTicket(data);
    } catch (err: any) {
      console.error('Error loading ticket:', err);
      showCustomAlert.error(err.response?.data?.message || 'Greška pri učitavanju tiketa');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: ITTicketStatus) => {
    if (!ticket) return;
    try {
      await itTicketsAPI.updateStatus(ticket._id, newStatus);
      await loadTicket();
    } catch (err: any) {
      console.error('Error updating status:', err);
      showCustomAlert.error(err.response?.data?.message || 'Greška pri promeni statusa');
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || !ticket) return;
    setSubmittingComment(true);
    try {
      await itTicketsAPI.addComment(ticket._id, commentText);
      setCommentText('');
      await loadTicket();
    } catch (err: any) {
      console.error('Error adding comment:', err);
      showCustomAlert.error(err.response?.data?.message || 'Greška pri dodavanju komentara');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleEditComment = async (commentId: string) => {
    if (!editCommentText.trim() || !ticket) return;
    try {
      await itTicketsAPI.updateComment(ticket._id, commentId, editCommentText);
      setEditingCommentId(null);
      setEditCommentText('');
      await loadTicket();
    } catch (err: any) {
      console.error('Error updating comment:', err);
      showCustomAlert.error(err.response?.data?.message || 'Greška pri izmeni komentara');
    }
  };

  const canEditStatus = user?.role === 'admin' || user?.role === 'direktor';
  const canEditComment = (commentUserId: string) => {
    return user?._id === commentUserId || canEditStatus;
  };

  if (loading || !ticket) {
    return <div style={{ padding: '2rem' }}>Učitavanje...</div>;
  }

  const statusOptions = [
    { label: 'Otvoren', value: 'open' },
    { label: 'U toku', value: 'in_progress' },
    { label: 'Rešen', value: 'resolved' },
    { label: 'Zatvoren', value: 'closed' }
  ];

  const problemTypeLabels: Record<string, string> = {
    hardware: 'Hardware',
    software: 'Software',
    network: 'Network',
    printer: 'Printer',
    email: 'Email',
    password: 'Password',
    access: 'Access',
    other: 'Other'
  };

  const statusLabels: Record<string, string> = {
    open: 'Otvoren',
    in_progress: 'U toku',
    resolved: 'Rešen',
    closed: 'Zatvoren'
  };

  const statusSeverity: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
    open: 'danger',
    in_progress: 'warning',
    resolved: 'info',
    closed: 'success'
  };

  // Kombinuj komentare i timeline u jedan niz za prikaz
  const timelineItems = [
    ...(ticket.timeline || []).map(t => ({
      type: 'timeline' as const,
      data: t
    })),
    ...(ticket.comments || []).map(c => ({
      type: 'comment' as const,
      data: c
    }))
  ].sort((a, b) => {
    const dateA = a.type === 'timeline' ? new Date(a.data.createdAt) : new Date(a.data.createdAt);
    const dateB = b.type === 'timeline' ? new Date(b.data.createdAt) : new Date(b.data.createdAt);
    return dateA.getTime() - dateB.getTime();
  });

  return (
    <div style={{ padding: '2rem', display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '2rem' }}>
      {/* Main Content */}
      <div>
        <div style={{ marginBottom: '1.5rem' }}>
          <Button
            icon="pi pi-arrow-left"
            label="Nazad"
            className="p-button-text"
            onClick={() => navigate('/it-tickets')}
            style={{ marginBottom: '1rem' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div>
              <h1 style={{ margin: 0, marginBottom: '0.5rem' }}>
                Ticket No. {ticket._id.substring(0, 6).toUpperCase()}
              </h1>
              <p style={{ margin: 0, color: '#6b7280' }}>Subject: {problemTypeLabels[ticket.problemType]}</p>
            </div>
            <Tag 
              value={statusLabels[ticket.status]} 
              severity={statusSeverity[ticket.status]}
              style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}
            />
          </div>
        </div>

        <TabView>
          <TabPanel header="Ticket History">
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ marginBottom: '1rem' }}>Description</h3>
              <div style={{ 
                padding: '1rem', 
                backgroundColor: '#f9fafb', 
                borderRadius: '4px',
                border: '1px solid #e5e7eb'
              }}>
                {ticket.description}
              </div>
            </div>

            <div>
              <h3 style={{ marginBottom: '1rem' }}>Ticket Info.</h3>
              {timelineItems.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                  Nema aktivnosti
                </div>
              ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {timelineItems.map((item, index) => {
                  if (item.type === 'timeline') {
                    const t = item.data;
                    if (t.action === 'status_changed') {
                      return (
                        <div
                          key={`timeline-${t._id}`}
                          style={{
                            padding: '1rem',
                            backgroundColor: 'white',
                            borderRadius: '4px',
                            border: '1px solid #e5e7eb',
                            display: 'flex',
                            gap: '1rem',
                            alignItems: 'flex-start'
                          }}
                        >
                          <Avatar
                            icon="pi pi-refresh"
                            style={{ backgroundColor: '#f59e0b', color: 'white' }}
                            size="normal"
                            shape="circle"
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                              {t.userName} {new Date(t.createdAt).toLocaleDateString('sr-RS')}
                            </div>
                            <div style={{ color: '#6b7280' }}>
                              Status promenjen sa "{statusLabels[t.fromStatus || '']}" na "{statusLabels[t.toStatus || '']}"
                            </div>
                          </div>
                        </div>
                      );
                    } else if (t.action === 'created') {
                      return (
                        <div
                          key={`timeline-${t._id}`}
                          style={{
                            padding: '1rem',
                            backgroundColor: 'white',
                            borderRadius: '4px',
                            border: '1px solid #e5e7eb',
                            display: 'flex',
                            gap: '1rem',
                            alignItems: 'flex-start'
                          }}
                        >
                          <Avatar
                            label={`${t.userName.split(' ')[0][0]}${t.userName.split(' ')[1]?.[0] || ''}`}
                            style={{ backgroundColor: '#9ca3af', color: 'white' }}
                            size="normal"
                            shape="circle"
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                              {t.userName} {new Date(t.createdAt).toLocaleDateString('sr-RS')}
                            </div>
                            <div style={{ color: '#6b7280' }}>Tiket kreiran</div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  } else {
                    const c = item.data;
                    const isEditing = editingCommentId === c._id;
                    return (
                      <div
                        key={`comment-${c._id}`}
                        style={{
                          padding: '1rem',
                          backgroundColor: 'white',
                          borderRadius: '4px',
                          border: '1px solid #e5e7eb',
                          display: 'flex',
                          gap: '1rem',
                          alignItems: 'flex-start'
                        }}
                      >
                        <Avatar
                          image={c.userId?.avatarUrl}
                          label={c.userId ? `${c.userId.firstName[0]}${c.userId.lastName[0]}` : c.userName?.[0] || '?'}
                          style={{ backgroundColor: '#9ca3af', color: 'white' }}
                          size="normal"
                          shape="circle"
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <div>
                              <div style={{ fontWeight: 'bold' }}>
                                {c.userId ? `${c.userId.firstName} ${c.userId.lastName}` : c.userName} {new Date(c.createdAt).toLocaleDateString('sr-RS')}
                              </div>
                              {c.editedAt && (
                                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                  Izmenjeno {new Date(c.editedAt).toLocaleDateString('sr-RS')}
                                </div>
                              )}
                            </div>
                            {c.userId && canEditComment(c.userId._id) && !isEditing && (
                              <Button
                                icon="pi pi-pencil"
                                className="p-button-text p-button-sm"
                                onClick={() => {
                                  setEditingCommentId(c._id);
                                  setEditCommentText(c.text);
                                }}
                              />
                            )}
                          </div>
                          {isEditing ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                              <InputTextarea
                                value={editCommentText}
                                onChange={(e) => setEditCommentText(e.target.value)}
                                rows={3}
                                style={{ width: '100%' }}
                              />
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <Button
                                  label="Sačuvaj"
                                  icon="pi pi-check"
                                  size="small"
                                  onClick={() => handleEditComment(c._id)}
                                />
                                <Button
                                  label="Otkaži"
                                  icon="pi pi-times"
                                  className="p-button-text"
                                  size="small"
                                  onClick={() => {
                                    setEditingCommentId(null);
                                    setEditCommentText('');
                                  }}
                                />
                              </div>
                            </div>
                          ) : (
                            <div style={{ color: '#374151' }}>{c.text}</div>
                          )}
                        </div>
                      </div>
                    );
                  }
                })}
              </div>
              )}
            </div>

            <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #e5e7eb' }}>
              <h3 style={{ marginBottom: '1rem' }}>Dodaj komentar:</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <InputTextarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Unesite komentar..."
                  rows={4}
                  style={{ width: '100%' }}
                />
                <Button
                  label="Submit"
                  icon="pi pi-send"
                  onClick={handleAddComment}
                  loading={submittingComment}
                  disabled={!commentText.trim()}
                />
              </div>
            </div>
          </TabPanel>
          <TabPanel header="Attachments">
            <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
              Nema priloga
            </div>
          </TabPanel>
        </TabView>
      </div>

      {/* Sidebar */}
      <div>
        <div style={{ 
          padding: '1.5rem', 
          backgroundColor: 'white', 
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: '#6b7280' }}>
              <i className="pi pi-user" />
              <span style={{ fontWeight: 'bold' }}>Prijavio</span>
            </div>
            <div>{ticket.reportedBy.firstName} {ticket.reportedBy.lastName}</div>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: '#6b7280' }}>
              <i className="pi pi-briefcase" />
              <span style={{ fontWeight: 'bold' }}>Tip problema</span>
            </div>
            <div>{problemTypeLabels[ticket.problemType]}</div>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: '#6b7280' }}>
              <i className="pi pi-list" />
              <span style={{ fontWeight: 'bold' }}>Status</span>
            </div>
            {canEditStatus ? (
              <Dropdown
                value={ticket.status}
                options={statusOptions}
                onChange={(e) => handleStatusChange(e.value)}
                appendTo="self"
                style={{ width: '100%' }}
              />
            ) : (
              <Tag 
                value={statusLabels[ticket.status]} 
                severity={statusSeverity[ticket.status]}
              />
            )}
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: '#6b7280' }}>
              <i className="pi pi-calendar" />
              <span style={{ fontWeight: 'bold' }}>Datum kreiranja</span>
            </div>
            <div>{new Date(ticket.createdAt).toLocaleDateString('sr-RS')}</div>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: '#6b7280' }}>
              <i className="pi pi-hashtag" />
              <span style={{ fontWeight: 'bold' }}>Ticket ID</span>
            </div>
            <div style={{ fontFamily: 'monospace' }}>{ticket._id.substring(0, 6).toUpperCase()}</div>
          </div>

          {canEditStatus && (
            <Button
              label="Update / Save"
              icon="pi pi-check"
              onClick={async () => {
                // Status se automatski čuva kada se promeni u dropdown-u
                await loadTicket();
              }}
              style={{ width: '100%', marginTop: '1rem' }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ITTicketDetail;

